import type { ContentItem } from "@/types/content";
import { movies } from "@/mocks/movies";
import { tvs } from "@/mocks/tvs";
import { news } from "@/mocks/news";
import { specials } from "@/mocks/specials";

export const movieList = movies as ContentItem[];
export const tvList = tvs as ContentItem[];
export const newsList = news as ContentItem[];
export const specialList = specials as ContentItem[];

export const allEntries: ContentItem[] = [
  ...movieList,
  ...tvList,
  ...newsList,
  ...specialList,
];

export function getEntryById(id: string | undefined): ContentItem | undefined {
  if (!id) return undefined;
  return allEntries.find((item) => item.id === id);
}

export function getEntriesByIds(ids: string[]): ContentItem[] {
  return ids
    .map((id) => getEntryById(id))
    .filter((item): item is ContentItem => Boolean(item));
}

function normalize(text: string): string {
  return text.trim().toLowerCase();
}

export function searchEntries(keyword: string): ContentItem[] {
  const q = normalize(keyword);
  if (!q) return [];
  return allEntries.filter((item) => {
    const haystack = [
      item.title,
      item.summary,
      item.cast,
      item.director,
      item.region,
      item.genres.join(" "),
      item.tags.join(" "),
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

function yearMatches(itemYear: string, filter: string): boolean {
  if (filter === "全部年份") return true;
  if (filter === "更早") return Number(itemYear) <= 2023;
  return itemYear === filter;
}

export interface FilterOptions {
  region: string;
  genre: string;
  year: string;
  status?: string;
  sort: string;
}

function applyCommonFilters(list: ContentItem[], opts: FilterOptions): ContentItem[] {
  const filtered = list.filter((item) => {
    const regionOk =
      opts.region === "全部地区" || item.region === opts.region ||
      (opts.region === "影视工厂" && item.region === "影视工厂");
    const genreOk = opts.genre === "全部类型" || item.genres.includes(opts.genre);
    const yearOk = yearMatches(item.year, opts.year);
    const statusOk =
      !opts.status || opts.status === "全部状态" || item.status === opts.status;
    return regionOk && genreOk && yearOk && statusOk;
  });
  return sortEntries(filtered, opts.sort);
}

export function sortEntries(list: ContentItem[], sort: string): ContentItem[] {
  const copy = [...list];
  if (sort === "rating") {
    copy.sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0));
  } else if (sort === "hot") {
    copy.sort((a, b) => (b.rating ?? 0) * (b.tags.length + 1) - (a.rating ?? 0) * (a.tags.length + 1));
  } else {
    copy.sort((a, b) => (a.releaseDate < b.releaseDate ? 1 : -1));
  }
  return copy;
}

export function filterMovieList(opts: FilterOptions): ContentItem[] {
  return applyCommonFilters(movieList, opts);
}

export function filterTvList(opts: FilterOptions): ContentItem[] {
  return applyCommonFilters(tvList, opts);
}

export function filterNewsList(category: string, sort: string): ContentItem[] {
  let list = newsList.filter(
    (item) => category === "全部" || item.genres.includes(category)
  );
  list = sortEntries(list, sort === "hot" ? "hot" : "release");
  return list;
}

export function filterSpecialList(tag: string): ContentItem[] {
  if (tag === "全部") return specialList;
  return specialList.filter((item) => item.tags.includes(tag) || item.genres.includes(tag));
}

export function getRelated(item: ContentItem, limit = 4): ContentItem[] {
  const scored = allEntries
    .filter((candidate) => candidate.id !== item.id && candidate.type === item.type)
    .map((candidate) => {
      let score = 0;
      candidate.genres.forEach((g) => {
        if (item.genres.includes(g)) score += 3;
      });
      candidate.tags.forEach((t) => {
        if (item.tags.includes(t)) score += 1;
      });
      return { candidate, score };
    })
    .sort((a, b) => b.score - a.score);
  const related = scored.map((entry) => entry.candidate).slice(0, limit);
  if (related.length >= limit) return related;
  const fallback = allEntries.filter(
    (candidate) => candidate.id !== item.id && candidate.type !== item.type
  );
  return [...related, ...fallback].slice(0, limit);
}

export function paginate<T>(list: T[], page: number, pageSize: number) {
  const total = list.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(Math.max(1, page), totalPages);
  const start = (current - 1) * pageSize;
  return {
    items: list.slice(start, start + pageSize),
    total,
    totalPages,
    current,
  };
}